<template>
  <v-bottom-sheet :value="value" scrollable @input="close">
    <v-card>
      <v-card-title class="text-subtitle-1 font-weight-bold">
        {{ $t(choosing ? 'onboarding.field.issue.title' : 'onboarding.field.issue.menu') }}
        <v-spacer />
        <v-btn icon :disabled="working" @click="close(false)"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>

      <v-card-text class="pb-6">
        <v-alert v-if="error" type="error" dense text>{{ error }}</v-alert>

        <!-- Le menu : trois gestes, gros et séparés. -->
        <template v-if="!choosing && !releasing">
          <v-btn block depressed height="40" color="error" class="mb-3 field-issue__button" @click="choosing = true">
            <v-icon left>mdi-alert-outline</v-icon>{{ $t('onboarding.field.issue.report') }}
          </v-btn>
          <v-btn v-if="mine" block outlined height="40" class="mb-3 field-issue__button" @click="releasing = true">
            <v-icon left>mdi-undo-variant</v-icon>{{ $t('onboarding.field.issue.release') }}
          </v-btn>
          <field-supervision-button block :contact="order.supervision" />
        </template>

        <!-- Rendre : la pose redevient libre pour un collègue ; le rendez-vous ne bouge pas. -->
        <template v-else-if="releasing">
          <p class="text-body-1">{{ $t('onboarding.field.issue.release-explained') }}</p>
          <v-btn block depressed height="40" color="primary" class="mb-3" :loading="working" @click="release">{{ $t('onboarding.field.issue.release-confirm') }}</v-btn>
          <v-btn block small text :disabled="working" @click="releasing = false">{{ $t('back') }}</v-btn>
        </template>

        <!-- Signaler : une raison d'un doigt, une note si besoin. -->
        <template v-else>
          <v-item-group v-model="reason">
            <v-item v-for="r in reasons" :key="r" v-slot="{ active, toggle }" :value="r">
              <v-card outlined class="mb-2 field-issue__reason" :class="{ 'field-issue__reason--active': active }" @click="toggle">
                <v-card-text class="d-flex align-center pa-3 text-body-1 text--primary">
                  <v-icon :color="active ? 'error' : undefined" class="mr-3">{{ active ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank' }}</v-icon>
                  {{ issueReasonLabel(r) }}
                </v-card-text>
              </v-card>
            </v-item>
          </v-item-group>
          <v-textarea
              v-model="note"
              :label="$t(reason === 'other' ? 'onboarding.field.issue.note-required' : 'onboarding.field.issue.note')"
              outlined auto-grow rows="2" hide-details class="mt-3 mb-2"
          />
          <p class="text-caption text--secondary">{{ $t('onboarding.field.issue.explained') }}</p>
          <v-btn block depressed height="40" color="error" class="mb-3" :loading="working" :disabled="!canReport" @click="report">{{ $t('onboarding.field.issue.send') }}</v-btn>
          <v-btn block small text :disabled="working" @click="choosing = false">{{ $t('back') }}</v-btn>
        </template>
      </v-card-text>
    </v-card>
  </v-bottom-sheet>
</template>

<script>
import FieldRepository from '@repository/FieldRepository'
import CommissioningMixin from '../CommissioningMixin'
import FieldMixin, { FIELD_ISSUE_REASONS } from './FieldMixin'
import FieldSupervisionButton from './FieldSupervisionButton.vue'

/**
 * « Un problème ? » : signaler pourquoi la pose ne peut pas se faire (le bureau la replanifie), la
 * rendre à un collègue, ou appeler la supervision. Signaler ou rendre libère la pose : l'écran
 * appelant revient à la liste (`done`, avec ce qui s'est passé).
 */
export default {
  name: 'FieldIssueSheet',

  components: { FieldSupervisionButton },

  mixins: [CommissioningMixin, FieldMixin],

  props: {
    value: { type: Boolean, default: false },
    order: { type: Object, required: true },
    /** La pose est la sienne : il peut la rendre. */
    mine: { type: Boolean, default: false },
  },

  data() {
    return { choosing: false, releasing: false, reason: null, note: '', working: false, error: null, reasons: FIELD_ISSUE_REASONS }
  },

  computed: {
    canReport() {
      return !!this.reason && (this.reason !== 'other' || this.note.trim() !== '')
    },
  },

  methods: {
    close(open) {
      if (open || this.working) return
      this.choosing = false
      this.releasing = false
      this.error = null
      this.$emit('input', false)
    },

    report() {
      return this.run(async () => {
        await FieldRepository.reportIssue(this.order.id, this.reason, this.note.trim() || null)
        this.$emit('done', 'reported')
      })
    },

    release() {
      return this.run(async () => {
        await FieldRepository.release(this.order.id)
        this.$emit('done', 'released')
      })
    },

    async run(action) {
      this.working = true
      this.error = null
      try {
        await action()
      } catch (e) {
        this.error = e?.response ? this.messageOf(e) : this.$t('onboarding.field.offline-action')
      } finally {
        this.working = false
      }
    },
  },
}
</script>

<style scoped>
.field-issue__button {
  justify-content: flex-start;
}
.field-issue__reason--active {
  border-color: var(--v-error-base) !important;
  border-width: 2px;
}
</style>
